import { useCallback, useEffect, useState } from 'react'
import {
  NormalPeoplePicker,
  IPersonaProps,
  IBasePickerSuggestionsProps,
  Label,
  Text,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import type { Owner } from '../types'

// IPersonaProps carrying the Owner's id so onChange can report it back.
interface OwnerPersona extends IPersonaProps {
  ownerId: number
}

const DEBOUNCE_MS = 300 // NormalPeoplePicker resolveDelay
const PAGE_SIZE = 50 // hard cap; overflow shows "N of M — keep typing"
const MIN_CHARS = 2

function initials(o: Owner): string {
  const g = o.givenName?.trim()?.[0]
  const s = o.surname?.trim()?.[0]
  if (g || s) return `${g ?? ''}${s ?? ''}`.toUpperCase()
  // Fallback when given/surname are NULL (guests, service accounts, manual owners).
  const parts = o.displayName.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return `${first}${last}`.toUpperCase()
}

function ownerToPersona(o: Owner): OwnerPersona {
  const departed = o.accountStatus !== 'Active' || !!o.departedAt
  const deptTitle = [o.department, o.jobTitle].filter(Boolean).join(' · ')
  return {
    key: o.ownerId,
    ownerId: o.ownerId,
    text: departed ? `${o.displayName} (departed)` : o.displayName,
    secondaryText: o.email,
    tertiaryText: deptTitle || undefined,
    imageInitials: initials(o),
  }
}

export interface OwnerPickerProps {
  label: string
  selectedOwnerId?: number
  onChange: (ownerId?: number) => void
  /** Hide a single owner from suggestions (e.g. current Primary so it can't also be Secondary). */
  excludeOwnerId?: number
  /** Hide multiple owners from suggestions (e.g. all already-assigned owners). */
  excludeOwnerIds?: number[]
  disabled?: boolean
}

/**
 * Server-side searchable single-owner picker (FluentUI NormalPeoplePicker, itemLimit 1).
 * Prefix-searches DisplayName/GivenName/Surname/Email via the API; resolves the currently
 * selected owner by id so it displays even when not in the search page (or departed).
 * See .claude/memory/task_owner_picker_searchable.md.
 */
export function OwnerPicker({ label, selectedOwnerId, onChange, excludeOwnerId, excludeOwnerIds, disabled }: OwnerPickerProps) {
  const [selected, setSelected] = useState<OwnerPersona[]>([])
  const [overflowNote, setOverflowNote] = useState('')

  // Seed/refresh the selected persona from the id (resolves even departed owners).
  useEffect(() => {
    let active = true
    if (!selectedOwnerId) {
      setSelected([])
      return
    }
    if (selected.length === 1 && selected[0].ownerId === selectedOwnerId) return
    apiService
      .getOwner(selectedOwnerId)
      .then((o) => {
        if (active && o) setSelected([ownerToPersona(o)])
      })
      .catch(() => {
        /* leave empty if the owner can't be resolved */
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOwnerId])

  const onResolveSuggestions = useCallback(
    async (filter: string): Promise<IPersonaProps[]> => {
      const term = filter.trim()
      if (term.length < MIN_CHARS) {
        setOverflowNote('')
        return []
      }
      try {
        const res = await apiService.getOwners({ search: term, pageSize: PAGE_SIZE })
        const rawItems = res.items || []
        const excluded = new Set([
          ...(excludeOwnerId != null ? [excludeOwnerId] : []),
          ...(excludeOwnerIds ?? []),
        ])
        const items = rawItems.filter((o) => !excluded.has(o.ownerId)).map(ownerToPersona)
        // Overflow = server had more than this page (compare the RAW page count, not the
        // post-exclude count, else removing the primary falsely triggers the note at exactly 50).
        setOverflowNote(
          res.totalCount > rawItems.length
            ? `Showing ${items.length} of ${res.totalCount} — keep typing to narrow.`
            : ''
        )
        return items
      } catch {
        return []
      }
    },
    [excludeOwnerId]
  )

  const handleChange = (items?: IPersonaProps[]) => {
    const next = ((items as OwnerPersona[]) || []).slice(0, 1)
    setSelected(next)
    setOverflowNote('')
    onChange(next.length ? next[0].ownerId : undefined)
  }

  const suggestionProps: IBasePickerSuggestionsProps = {
    suggestionsHeaderText: 'Matching owners',
    noResultsFoundText: 'No matching owners',
    loadingText: 'Searching…',
  }

  return (
    <div>
      <Label disabled={disabled}>{label}</Label>
      <NormalPeoplePicker
        onResolveSuggestions={onResolveSuggestions}
        getTextFromItem={(p: IPersonaProps) => p.text || ''}
        pickerSuggestionsProps={suggestionProps}
        selectedItems={selected}
        onChange={handleChange}
        itemLimit={1}
        disabled={disabled}
        resolveDelay={DEBOUNCE_MS}
        inputProps={{
          placeholder: selected.length ? '' : 'Type a name or email…',
          'aria-label': label,
        }}
      />
      {overflowNote && (
        <Text variant="small" styles={{ root: { color: '#605e5c', marginTop: 4 } }}>
          {overflowNote}
        </Text>
      )}
    </div>
  )
}
